import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { markdownComponents } from '../AIAgent/AIAgentMarkdown';
import './SharedChatView.css';

function formatRelative(iso) {
  if (!iso) return '';
  var d = new Date(iso);
  var s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return Math.floor(s / 60) + ' min ago';
  if (s < 86400) return Math.floor(s / 3600) + ' h ago';
  if (s < 86400 * 7) return Math.floor(s / 86400) + ' days ago';
  return d.toLocaleDateString();
}

function SharedChatView() {
  var params = useParams();
  var search = useSearchParams()[0];
  var shortcode = params.shortcode;
  var [state, setState] = useState({ loading: true, data: null, error: null });
  // Note copy button state — declared unconditionally (hooks before any early return)
  var noteDocRef = useRef(null);
  var [noteCopied, setNoteCopied] = useState('');

  // The app-wide viewport meta disables pinch-zoom (maximum-scale=1, user-scalable=no)
  // — on a PUBLIC shared page a recipient trying to zoom a table gets a dead page
  // under their fingers. Allow zooming here; restore the app's meta on unmount.
  useEffect(function() {
    var meta = document.querySelector('meta[name=viewport]');
    if (!meta) return undefined;
    var original = meta.getAttribute('content');
    meta.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=5, user-scalable=yes, viewport-fit=cover');
    return function() { meta.setAttribute('content', original); };
  }, []);

  useEffect(function() {
    var chain = search.get('share_chain');
    var url = '/api/s/' + encodeURIComponent(shortcode) + (chain ? '?share_chain=' + encodeURIComponent(chain) : '');
    fetch(url)
      .then(function(res) {
        if (res.status === 404) return res.json().then(function(){ throw new Error('not_found'); });
        if (res.status === 410) return res.json().then(function(j){ throw new Error(j.error || 'gone'); });
        if (!res.ok) throw new Error('fetch_failed');
        return res.json();
      })
      .then(function(data) { setState({ loading: false, data: data, error: null }); })
      .catch(function(err) { setState({ loading: false, data: null, error: err.message }); });
  }, [shortcode]);

  function reshare(platform) {
    var url = window.location.origin + window.location.pathname + '?utm_source=reshare&utm_medium=' + platform + '&share_chain=' + shortcode;
    var firstMsg = state.data && state.data.snapshot && state.data.snapshot.messages && state.data.snapshot.messages[0];
    var isNote = !!(state.data && state.data.snapshot && state.data.snapshot.sourceLabel === 'Notes share');
    var kind = isNote ? 'note' : 'AI Assistant conversation';
    var preview = (firstMsg && firstMsg.content) ? firstMsg.content.slice(0, 60).replace(/\s+/g, ' ').trim() + '…' : kind;
    var text = 'Check out this ' + kind + ' — ' + preview;
    if (platform === 'copy') {
      try { navigator.clipboard.writeText(url); } catch(e) { window.prompt('Copy this link:', url); }
      return;
    }
    if (platform === 'x') return window.open('https://twitter.com/intent/tweet?text=' + encodeURIComponent(text + ' ' + url), '_blank', 'noopener');
    if (platform === 'linkedin') return window.open('https://www.linkedin.com/sharing/share-offsite/?url=' + encodeURIComponent(url), '_blank', 'noopener');
    if (platform === 'email') return window.open('mailto:?subject=' + encodeURIComponent(isNote ? 'Shared note' : 'AI Assistant conversation') + '&body=' + encodeURIComponent(text + '\n\n' + url));
    if (platform === 'whatsapp') return window.open('https://wa.me/?text=' + encodeURIComponent(text + ' ' + url), '_blank', 'noopener');
  }

  if (state.loading) {
    return (
      <div className="shared-chat-page">
        <div className="shared-chat-loading">Loading…</div>
      </div>
    );
  }

  if (state.error) {
    var msg = state.error === 'revoked' ? 'This share has been revoked.'
      : state.error === 'expired' ? 'This share has expired.'
      : state.error === 'not_found' ? 'This share does not exist or has been removed.'
      : 'Could not load this share.';
    return (
      <div className="shared-chat-page">
        <header className="shared-chat-topbar">
          <a href="/" className="shared-chat-brand"><img src="/assets/dynamic/NumFreeSymbol.png" alt="NumFree" /></a>
          <a href="/signup" className="shared-chat-try">Try it →</a>
        </header>
        <div className="shared-chat-error">
          <h2>{msg}</h2>
          <a href="/" className="shared-chat-cta-btn">Discover NumFree</a>
        </div>
      </div>
    );
  }

  var data = state.data;
  var messages = (data.snapshot && data.snapshot.messages) || [];
  var sharer = data.snapshot && data.snapshot.sharerName;
  if (sharer && String(sharer).indexOf('@') > -1) sharer = ''; // never show emails as sharer
  var when = formatRelative(data.createdAt);
  var isNoteShare = !!(data.snapshot && data.snapshot.sourceLabel === 'Notes share');
  var eyebrow = isNoteShare ? 'Shared note' : 'AI Assistant conversation';
  // A note is a document, not a chat — render it as a clean article, not a giant bubble.
  var noteBody = isNoteShare ? ((messages.filter(function(m){ return m.role === 'user'; })[0] || messages[0] || {}).content || '') : '';

  // Copy button (top-right of the note): click = RICH text (html + plain flavors,
  // pastes into email with formatting); Shift+click = plain text only.
  var copyNote = function(e) {
    var art = noteDocRef.current;
    if (!art) return;
    var plain = (art.innerText || '').replace(/\n{3,}/g, '\n\n').trim();
    var done = function(label) {
      setNoteCopied(label);
      setTimeout(function() { setNoteCopied(''); }, 1600);
    };
    var plainOnly = function() {
      navigator.clipboard.writeText(plain).then(function() { done('Copied as text'); },
        function() { done('Copy failed'); });
    };
    if (e.shiftKey || !navigator.clipboard || !window.ClipboardItem) return plainOnly();
    var html = '<meta charset="utf-8">' + art.innerHTML;
    navigator.clipboard.write([new ClipboardItem({
      'text/plain': new Blob([plain], { type: 'text/plain' }),
      'text/html': new Blob([html], { type: 'text/html' })
    })]).then(function() { done('Copied rich ✓'); }, plainOnly);
  };

  return (
    <div className="shared-chat-page">
      <header className="shared-chat-topbar">
        <a href="/" className="shared-chat-brand">
          <img src="/assets/dynamic/NumFreeSymbol.png" alt="NumFree" />
          <span>NumFree</span>
        </a>
        <a href="/signup?ref=share" className="shared-chat-try">Try it →</a>
      </header>

      <main className="shared-chat-main">
        <div className="shared-chat-header">
          <div className="shared-chat-eyebrow">{eyebrow}</div>
          <div className="shared-chat-meta">
            {sharer ? ('shared by ' + sharer + ' · ') : ''}
            {when}
          </div>
          {isNoteShare && (
            <button
              type="button"
              className="shared-note-copy-btn"
              data-testid="note-copy-btn"
              onClick={copyNote}
              title="Copy as rich text — Shift+Click copies as plain text"
            >
              {noteCopied ? noteCopied : '⧉ Copy'}
            </button>
          )}
        </div>

        {isNoteShare ? (
          <article className="shared-note-doc" ref={noteDocRef}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{noteBody}</ReactMarkdown>
          </article>
        ) : (
        <div className="shared-chat-thread">
          {messages.map(function(m, i) {
            var node = (
              <div key={i} className={'shared-bubble shared-bubble-' + m.role}>
                {m.role === 'assistant' && (
                  <div className="shared-bubble-avatar" aria-hidden="true">✨</div>
                )}
                <div className="shared-bubble-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{m.content}</ReactMarkdown>
                </div>
              </div>
            );
            // After 2nd assistant reply, inject inline promo card
            var assistantIdx = messages.slice(0, i + 1).filter(function(x){return x.role==='assistant';}).length;
            if (m.role === 'assistant' && assistantIdx === 2) {
              return (
                <React.Fragment key={i}>
                  {node}
                  <div className="shared-chat-inline-promo">
                    <div className="shared-chat-inline-promo-text">
                      <strong>✨ This is NumFree's AI Assistant.</strong>
                      <div>It answers calls, sends texts, books meetings, and handles your number for you.</div>
                    </div>
                    <a href="/signup?ref=share" className="shared-chat-inline-promo-cta">See how it works →</a>
                  </div>
                </React.Fragment>
              );
            }
            return node;
          })}
        </div>
        )}

        <div className="shared-chat-footer">
          <div className="shared-chat-reshare">
            <div className="shared-chat-reshare-title">Loved this? Share it forward.</div>
            <div className="shared-chat-reshare-row">
              <button onClick={function(){ reshare('copy'); }} className="shared-chat-reshare-btn" title="Copy link">📋 Copy link</button>
              <button onClick={function(){ reshare('x'); }} className="shared-chat-reshare-btn" title="X / Twitter">X</button>
              <button onClick={function(){ reshare('linkedin'); }} className="shared-chat-reshare-btn" title="LinkedIn">in</button>
              <button onClick={function(){ reshare('email'); }} className="shared-chat-reshare-btn" title="Email">✉</button>
              <button onClick={function(){ reshare('whatsapp'); }} className="shared-chat-reshare-btn" title="WhatsApp">WhatsApp</button>
            </div>
          </div>
          <div className="shared-chat-signup">
            <div className="shared-chat-signup-line">Want your own AI Assistant + phone number?</div>
            <a href="/signup?ref=share" className="shared-chat-signup-cta">Get started free →</a>
          </div>
          <div className="shared-chat-byline">Shared via NumFree · <a href="https://numfree.com">numfree.com</a></div>
        </div>
      </main>
    </div>
  );
}

export default SharedChatView;
